import { curry, type PlatformEvent } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import { hide, show } from 'lib/sequence/component/state/hidden'
import sequenceComponentState from 'lib/sequence/component/state'
import { set } from 'lib/sequence/component/state/value'
import showToast from 'app/_shared/component/showToast'
import { scale } from 'app/xEcoApm/helper'
import { MEAS_CTRL_OFF, MEAS_CTRL_ON, MEAS_DURATION, MEAS_RES } from 'app/xEcoApm/helper/apm'

const {
  set: setCount,
} = sequenceComponentState('count')

/**
 * Displays a toast informing the user that a measurement process couldn't be completed due to an
 * error.
 *
 * @param module current module
 */
const showErrorToast = async (module: Gaia.AppModule.Spec) => await showToast(module, {
  severity: 'error',
  message: {
    ns: 'apmGo',
    key: 'toast.measurement.error.message',
    defaultValue: 'Measurement error'
  },
  description: {
    ns: 'apmGo',
    key: 'toast.measurement.error.description',
    defaultValue: 'Please, check the device\'s connectivity and try again.'
  }
})

/**
 * Shows a toast informing the user that a measurement is done.
 *
 * @param module current module
 */
const showDoneToast = async (module: Gaia.AppModule.Spec) => await showToast(module, {
  message: {
    ns: 'apmGo',
    key: 'toast.measurement.success.message',
    defaultValue: 'Measurement finished'
  },
})

/**
 * Shows a toast informing the user that a measurement has been cancelled.
 *
 * @param module current module
 */
const showCancelToast = async (module: Gaia.AppModule.Spec) => await showToast(module, {
  message: {
    ns: 'apmGo',
    key: 'toast.measurement.cancel.message',
    defaultValue: 'Measurement cancelled'
  },
})

/**
 * Updates the action's UI according to the `measurement` data.
 *
 * @param module      current module
 * @param components  {@link find} proxy of the current action component
 * @param measurement measurement data
 */
const update = async (
  module: Gaia.AppModule.Spec,
  components: Record<string, Gaia.Component.Spec|null>,
  measurement: any,
): Promise<void> => {
  const { component, progressBar, frequencyWidget, apmWidget, form } = components
  const { btnStart, btnStop, btnSave } = find(component![G.ACTIONS][0])

  if (!measurement) {
    hide(btnStop)
    show(btnStart)
  } else if (measurement?.m === 0) {
    set(progressBar, 0)
    hide(btnStop)
    show(btnSave)
    show(form)
  }

  const progressValue = measurement?.d ? measurement?.d / MEAS_DURATION * 100 : 0
  const frequencyValue = scale(measurement?.f, undefined, 1)
  const apmValue = measurement?.a

  set(progressBar, progressValue)
  setCount(frequencyWidget, frequencyValue)
  setCount(apmWidget, apmValue)

  await module[G.ADAPTER][G.UI].update(module)
}

/**
 * Resets process' start and stop state flags.
 *
 * @param module    current module
 * @param cancelled whether the process was manually cancelled
 */
const resetState = (module: Gaia.AppModule.Spec, cancelled: boolean)=> {
  const actionState = module[G.STATE][G.ACTION][G.STATE]
  actionState.onMeasurementStopped?.(cancelled)
  delete actionState.measuring
  delete actionState.stopMeasurement
  delete actionState.onMeasurementStopped
}

/**
 * Recursively checks the measurement in progress by polling its state from the device.
 *
 * Uses `update` to keep the action's UI in sync.
 *
 * @param module      current module
 * @param components  {@link find} proxy of the current action component
 * @param peripheral  bluetooth peripheral (current model's `sensor`)
 */
const poll = async (
  module: Gaia.AppModule.Spec,
  components: Record<string, Gaia.Component.Spec|null>,
  peripheral: any,
): Promise<void> => {
  const actionState = module[G.STATE][G.ACTION][G.STATE]

  setTimeout(async () => {
    try {
      if (!actionState.stopMeasurement) {
        const response = await peripheral[G.API].send(MEAS_RES)
        await update(module, components, response)
        if (response?.m === 1) {
          await poll(module, components, peripheral)
        } else {
          await showDoneToast(module)
          resetState(module, false)
        }
      } else {
        const response = await peripheral[G.API].send(MEAS_CTRL_OFF)
        console.log('Measurement stopped by the user', response)
        await update(module, components, null)
        await showCancelToast(module)
        resetState(module, true)
      }
    } catch (e) {
      console.log('Error during measurement', e)
      await update(module, components, null)
      await showErrorToast(module)
      resetState(module, false)
    }
  }, 500)
}

/**
 * Starts checking the state of a measurement in progress.
 *
 * @param module  current module
 */
const startMeasurementCheck = async (module: Gaia.AppModule.Spec) => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { sensor } = module[G.MODEL][G.CHILDREN]
  const { value: { name } } = sensor[G.CACHE]
  const bluetoothAdapter = module[G.ADAPTER][G.BLUETOOTH]
  const peripheral = bluetoothAdapter[G.GROUP][name]

  const components = find(actionComponent)

  await poll(module, components, peripheral)
}

/**
 * Starts a measurement.
 *
 * Instructs a bluetooth peripheral (current model's `sensor`) to start a measurement. Keeps the
 * action's UI updated by periodically polling the current measurement's state until it's done.
 *
 * @param module    current module
 * @param component event source
 * @param event     triggered event
 */
const startMeasurement = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec | null,
  event: PlatformEvent | null
) => {
  const moduleState = module[G.STATE]
  const action = module[G.STATE][G.ACTION]
  const { btnStart, btnStop } = find(action[G.COMPONENT][G.ACTIONS][0])

  const actionState = action[G.STATE]
  const bluetoothAdapter = module[G.ADAPTER][G.BLUETOOTH]
  const { sensor } = module[G.MODEL][G.CHILDREN]
  const { value: { name } } = sensor[G.CACHE]
  const peripheral = bluetoothAdapter[G.GROUP][name]

  resetState(module, false)
  actionState.measuring = true

  try {
    hide(btnStart)
    show(btnStop)
    await module[G.ADAPTER][G.UI].update(module)

    const response = await peripheral[G.API].send(MEAS_CTRL_ON)
    moduleState.scalingFactor = response.f

    await startMeasurementCheck(module)

  } catch (e) {
    hide(btnStop)
    show(btnStart)
    resetState(module, false)
    await module[G.ADAPTER][G.UI].update(module)
    console.error('Error during measurement', e)
    await showErrorToast(module)
  }
}

export default curry(startMeasurement)
